import { ComponentProps, useMemo } from 'react'
import { DropItemViewMode, DropItemViewProps } from './DropItem.types'
import Styles from './DropItem.module.scss'
import useHero from '@onepercentio/one-ui/hooks/useHero'
import Text from '@onepercentio/one-ui/components/Text'
import { tO } from 'translate/i18n'
import { FigmaTypo } from 'containers/OneUIProvider/OneUIProvider'
import { offerPriceParameters } from 'components/offer/OfferPrice'
import UncontrolledTransition from '@onepercentio/one-ui/components/UncontrolledTransition'
import { TransitionAnimationTypes } from '@onepercentio/one-ui/components/Transition'
import Loader from '@onepercentio/one-ui/components/Loader'
import { OfferType } from 'core/logic/drop/drop.types'
import {
  useIPODetails,
  useIPOInformation,
} from 'pages/Unauthenticated/Drops/Details/Models/IPO/IPO.hooks'
import BigNumber from 'bignumber.js'
import { useCounterForOffer } from 'context/Counters'
import { goalReachedLabel } from 'pages/Unauthenticated/Drops/Details/Models/IPO/IPO.view'
import Button from '@onepercentio/one-ui/components/Button'
import { useAssetOwners } from 'context/Asset'
import { formatDateOnly, numberFormatter } from 'core/helpers/formatter'
import Countdown from '@onepercentio/one-ui/components/Countdown'
import { CountdownTextModel } from '@onepercentio/one-ui/components/Countdown'
import Tag from 'components/Tag'
import LoaderDotsIndicator from '@onepercentio/one-ui/components/LoaderDotsIndicator'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import { DROP_ITEM_TEST_IDS } from './DropItem.e2e'
import { useDropItemInfo } from './DropItem.hook'
import OfferPriceLogic from 'components/offer/OfferPrice/OfferPrice.logic'
import { ONE_DAY_DELAY } from 'utility/timer'

const CONTAINER_MDOES = [Styles.expanded, Styles.short]

/**
 * Shows a drop item
 **/
export default function DropItemView(props: DropItemViewProps) {
  return (
    <DropViewByType
      {...props}
      view={{ CVM: IPODropItem, Generic: GenericDropItem }}
    />
  )
}

export function DropViewByType(
  props: DropItemViewProps & {
    view: {
      CVM: typeof IPODropItem
      Generic: typeof GenericDropItem
    }
  }
) {
  const { currency, image, intl, price, prices, isCvm } = useDropItemInfo(props)
  if (isCvm)
    return (
      <props.view.CVM
        offer={props.dropOrAsset as OfferType}
        mode={props.mode}
        heroSuffix={props.heroSuffix}
        onClick={props.onClick}
        data-testid={props['data-testid']}
      />
    )
  else
    return (
      <props.view.Generic
        {...props}
        image={image}
        currency={currency}
        prices={prices}
        price={price}
        intl={intl}
        data-testid={props['data-testid']}
      />
    )
}

export function IPODropItem({
  offer,
  onClick,
  mode,
  heroSuffix,
  ...props
}: { offer: OfferType } & Pick<
  ComponentProps<typeof DropItemView>,
  'onClick' | 'mode' | 'heroSuffix' | 'data-testid'
>) {
  const { heroRef } = useGalleryHero(offer, mode, heroSuffix)
  const reservedUnits = useCounterForOffer(offer.id)
  const { goal, goalUnits, unitPrice } = useIPOInformation(offer)

  const { currencyFormatter, intl, quotasProgress } = useIPODetails(offer, [
    offer.supply,
    goalUnits,
    reservedUnits,
  ])

  const goalInPrice = useMemo(() => {
    return quotasProgress.toReserved === undefined
      ? undefined
      : {
          current: new BigNumber(goal)
            .multipliedBy(
              new BigNumber(quotasProgress.toReserved!).dividedBy(100)
            )
            .toNumber(),
        }
  }, [quotasProgress, goal])

  const assetOwners = useAssetOwners().ownersByTokenId
  const ownersForThisAsset = assetOwners
    ? assetOwners[offer.asset.tokenId]?.length ?? 0
    : assetOwners

  const dataTable = useMemo(() => {
    const investorsSection = [
      tO('offerDetails.investors'),
      ownersForThisAsset === undefined ? (
        <LoaderDotsIndicator dotsCount={5} />
      ) : ownersForThisAsset === 0 ? (
        tO('offerDetails.beTheFirstToInvest')
      ) : (
        numberFormatter(ownersForThisAsset!)
      ),
    ] as const
    switch (offer.cvm88.type) {
      case 'equity':
        return [
          [
            tO('offerDetails.reachedPrice'),
            goalInPrice === undefined ? (
              <LoaderDotsIndicator dotsCount={5} />
            ) : (
              currencyFormatter.format(goalInPrice.current)
            ),
          ],
          [tO('offerDetails.goal'), currencyFormatter.format(goal)],
          [
            tO('offerDetails.minInvestmentShort'),
            currencyFormatter.format(unitPrice),
          ],
          investorsSection,
        ] as const
      case 'fixed':
      case 'receivable':
        const timeRemaining = offer.expire!.getTime() - Date.now()
        return [
          [
            tO('offerDetails.receivable.soldUnits'),
            goalInPrice === undefined ? (
              <LoaderDotsIndicator dotsCount={5} />
            ) : (
              currencyFormatter.format(goalInPrice.current)
            ),
          ],
          [
            tO('offerDetails.receivable.availability'),
            currencyFormatter.format(goal),
          ],
          investorsSection,
          [
            tO('offerDetails.receivable.expiration'),
            [
              formatDateOnly(offer.expire!),
              timeRemaining < ONE_DAY_DELAY * 3 && (
                <Countdown
                  timeRemaining={timeRemaining}
                  model={CountdownTextModel.SHORT}
                />
              ),
            ],
          ],
        ] as const
      default:
        throw new Error(
          `The cvm88 type "${(offer as any).cvm88.type}" is not configured yet`
        )
    }
  }, [offer, ownersForThisAsset])

  const { goalCls, goalActionLbl } = useMemo(() => {
    if (quotasProgress.toReserved! >= 100)
      return {
        goalCls: Styles.complete,
        goalActionLbl: 'offerDetails.actions.moreInfo',
      } as const
    return {
      goalCls: '',
      goalActionLbl: 'offerDetails.actions.invest',
    } as const
  }, [quotasProgress.toReserved])

  return (
    <div
      ref={heroRef}
      className={`${Styles.asset} ${mode in Styles ? Styles[mode] : ''} ${
        Styles.cvm
      }`}
      data-testid={props['data-testid']}>
      <div style={{ backgroundImage: `url(${offer.media.image})` }}>
        <div>
          <img src={offer.media.icon} alt={intl.title} />
          <div className='dark'>
            <Text type='h3' className={'dark'}>
              {intl.title}
            </Text>
            <Spacing size='8' />
            <Tag className={`${Styles[offer.cvm88.type]} dark`} size='small'>
              <Text type='p12'>
                {tO(`offerDetails.cvm.types.${offer.cvm88.type}`)}
              </Text>
            </Tag>
          </div>
        </div>
        <div className={`${Styles.goalProgress} ${goalCls} dark`}>
          <Text type='p14'>
            <b>{goalReachedLabel(offer.cvm88.type, quotasProgress)}</b>
            {'estimatedReturn' in offer.cvm88 && (
              <span>
                {tO('offerDetails.cvm.performance', {
                  performance: `${numberFormatter(
                    offer.cvm88.estimatedReturn
                  )}`,
                })}
              </span>
            )}
          </Text>
        </div>
      </div>
      <div>
        {dataTable.map(([lbl, val]) => (
          <div className={Styles.row} key={lbl}>
            <Text type='p14'>{lbl}</Text>
            <div>
              {Array.isArray(val) ? (
                <>
                  {val.map((val, i) => (
                    <Text type='p14' key={String(i)}>
                      <b>{val}</b>
                    </Text>
                  ))}
                </>
              ) : (
                <Text type='p14'>
                  <b>{val}</b>
                </Text>
              )}
            </div>
          </div>
        ))}
        <Button
          data-testid={DROP_ITEM_TEST_IDS.ACTION}
          onClick={onClick}
          color='secondary'
          variant='filled'>
          {tO(goalActionLbl)}
        </Button>
      </div>
    </div>
  )
}

export function GenericDropItem({
  image,
  intl,
  price,
  prices,
  currency,
  ...props
}: ComponentProps<typeof DropItemView> & {
  image: string | undefined
  intl: { name: string } | { title: string }
  prices:
    | {
        [c: string]: number
      }
    | undefined
  price: number | undefined
  currency: string | undefined
}) {
  const { heroRef } = useGalleryHero(
    props.dropOrAsset,
    props.mode,
    props.heroSuffix
  )
  return (
    <div
      ref={heroRef}
      className={`${Styles.asset} ${
        props.mode in Styles ? Styles[props.mode] : ''
      }`}
      style={{ backgroundImage: `url(${image})` }}
      data-testid={DROP_ITEM_TEST_IDS.CLICKABLE}
      onClick={props.onClick}>
      <div>
        <div>
          <Text type={FigmaTypo.H4}>
            {'name' in intl ? intl.name : intl.title}
          </Text>
          {prices && (
            <>
              <Text type={FigmaTypo.P16}>
                <OfferPriceLogic
                  {...offerPriceParameters({
                    currency,
                    unitPrice: price,
                    prices,
                  })}
                  size='md'
                  preferredCurrency={currency as any}
                  mode='text'
                />
              </Text>
              {!props.counters ? null : (
                <UncontrolledTransition
                  transitionType={TransitionAnimationTypes.COIN_FLIP}>
                  {props.counters.remaining === undefined ? (
                    <Loader key='loading' />
                  ) : (
                    <Text key={props.counters.remaining} type={FigmaTypo.P14}>
                      {tO(
                        props.counters.remaining === 0
                          ? 'components.drop.soldout'
                          : 'components.drop.remaining',
                        {
                          count: props.counters.remaining,
                        }
                      )}
                    </Text>
                  )}
                </UncontrolledTransition>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function useGalleryHero(
  assetOrOffer: DropItemViewProps['dropOrAsset'],
  mode: DropItemViewMode,
  suffix: string = ''
) {
  return useHero(`drop-${assetOrOffer.id}-${suffix || ''}`, undefined, {
    onHeroDetect: (h) => {
      if (
        document.querySelectorAll('[data-orderableid="orderable-list-clone"]')
          .length
      )
        return false
      return true
    },
    onHeroStart: (clone) => {
      clone.style.margin = '0px'
      const toAdd = mode in Styles ? Styles[mode] : ''
      const toRemove = CONTAINER_MDOES.filter((a) => a !== toAdd)
      for (let cls of toRemove) clone.classList.remove(cls)
      clone.classList.add(toAdd)
    },
  })
}

export const BaseExpandedWidth = Number(Styles.baseExpandedWidth)
export const BaseAssetWidth = Number(Styles.baseAssetWidth)
